import { ScenePreview } from "@cva/renderer";
import { extractPages } from "@cva/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, CircleAlert, ExternalLink, Loader2, MinusCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import { client, orpc } from "../lib/api";
import { cn, errorMessage } from "../lib/utils";
import { useEnv } from "./env";
import { useEditor, useEditorApi } from "./store";

/**
 * „Seiten als eigene Dateien“: chosen pages of a design become new files in
 * the same project – one file per page, or all chosen pages in one file.
 * The plan limit stops the rest with a clear message.
 */

interface Job {
  key: string;
  label: string;
  pages: number[];
  title: string;
}

type Status =
  | { state: "pending" }
  | { state: "running" }
  | { state: "done"; documentId: string; title: string }
  | { state: "error"; message: string }
  | { state: "skipped" };

function pagesLabel(pages: readonly number[]): string {
  return pages.length === 1
    ? `Seite ${pages[0]! + 1}`
    : `Seiten ${pages.map((page) => page + 1).join(", ")}`;
}

export function SplitPagesDialog({
  open,
  onOpenChange,
  beforeCreate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  beforeCreate?: () => Promise<unknown>;
}) {
  const env = useEnv();
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [together, setTogether] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [status, setStatus] = useState<Record<string, Status>>({});
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set([store.getState().pageIndex]));
    setTogether(false);
    setJobs([]);
    setStatus({});
  }, [open, store]);

  const pageCount = doc.pages.length;
  const chosen = [...selected].filter((index) => index < pageCount).sort((a, b) => a - b);
  const toggle = (index: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  const start = async () => {
    if (chosen.length === 0) return;
    const list: Job[] =
      together || chosen.length === 1
        ? [
            {
              key: "all",
              label: pagesLabel(chosen),
              pages: chosen,
              title: `${env.title} – ${pagesLabel(chosen)}`,
            },
          ]
        : chosen.map((index) => ({
            key: String(index),
            label: pagesLabel([index]),
            pages: [index],
            title: `${env.title} – ${doc.pages[index]?.name || pagesLabel([index])}`,
          }));
    setJobs(list);
    setStatus(Object.fromEntries(list.map((job) => [job.key, { state: "pending" }])));
    setRunning(true);
    try {
      await beforeCreate?.();
      const source = store.getState().doc;
      let stopped = false;
      for (const job of list) {
        if (stopped) {
          setStatus((current) => ({ ...current, [job.key]: { state: "skipped" } }));
          continue;
        }
        setStatus((current) => ({ ...current, [job.key]: { state: "running" } }));
        try {
          const created = await client.documents.create({
            orgId: env.orgId,
            projectId: env.projectId,
            title: job.title.slice(0, 200),
            content: extractPages(source, job.pages),
          });
          setStatus((current) => ({
            ...current,
            [job.key]: { state: "done", documentId: created.id, title: created.title },
          }));
        } catch (error) {
          setStatus((current) => ({
            ...current,
            [job.key]: { state: "error", message: errorMessage(error) },
          }));
          const code = (error as { code?: string } | null)?.code;
          if (code === "QUOTA_EXCEEDED" || code === "FORBIDDEN" || code === "TOO_MANY_REQUESTS") {
            stopped = true;
          }
        }
      }
    } finally {
      setRunning(false);
      void queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
    }
  };

  const started = jobs.length > 0;
  const doneCount = Object.values(status).filter((entry) => entry.state === "done").length;
  const thumb = doc.format.width >= doc.format.height ? 150 : 110;

  return (
    <Dialog open={open} onOpenChange={(next) => !running && onOpenChange(next)}>
      <DialogContent
        title="Seiten als eigene Dateien"
        description="Die gewählten Seiten werden als neue Dateien im selben Projekt angelegt. Diese Datei bleibt unverändert."
        className="max-w-3xl"
      >
        {!started ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                className="rounded-md px-2 py-1 font-medium text-brand-700 hover:bg-brand-50"
                onClick={() => setSelected(new Set(doc.pages.map((_page, index) => index)))}
              >
                Alle auswählen
              </button>
              <button
                type="button"
                className="rounded-md px-2 py-1 text-zinc-600 hover:bg-zinc-100"
                onClick={() => setSelected(new Set())}
              >
                Keine
              </button>
              <span className="ml-auto text-zinc-500">
                {chosen.length} von {pageCount} {pageCount === 1 ? "Seite" : "Seiten"} gewählt
              </span>
            </div>
            <div className="grid max-h-[50vh] grid-cols-3 gap-3 overflow-y-auto p-1 sm:grid-cols-4 md:grid-cols-5">
              {doc.pages.map((page, index) => {
                const active = selected.has(index);
                return (
                  <label
                    key={page.id}
                    className={cn(
                      "flex cursor-pointer flex-col items-center gap-1.5 rounded-lg border-2 p-2 transition",
                      active
                        ? "border-brand-500 bg-brand-50"
                        : "border-zinc-200 hover:border-zinc-300",
                    )}
                  >
                    <ScenePreview
                      document={doc}
                      pageIndex={index}
                      width={thumb}
                      resolveAsset={env.resolveAsset}
                      className="rounded shadow-sm ring-1 ring-zinc-200"
                    />
                    <span className="flex items-center gap-1.5 text-xs">
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggle(index)}
                        className="accent-brand-600"
                      />
                      {page.name || `Seite ${index + 1}`}
                    </span>
                  </label>
                );
              })}
            </div>
            {chosen.length > 1 ? (
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="split-mode"
                    checked={!together}
                    onChange={() => setTogether(false)}
                    className="accent-brand-600"
                  />
                  Jede Seite als eigene Datei ({chosen.length} Dateien)
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="split-mode"
                    checked={together}
                    onChange={() => setTogether(true)}
                    className="accent-brand-600"
                  />
                  Zusammen in einer neuen Datei
                </label>
              </div>
            ) : null}
          </div>
        ) : (
          <ul className="max-h-[60vh] space-y-1.5 overflow-y-auto">
            {jobs.map((job) => {
              const entry = status[job.key] ?? { state: "pending" };
              return (
                <li
                  key={job.key}
                  className="flex items-center gap-3 rounded-lg border border-zinc-200 px-3 py-2 text-sm"
                >
                  <span className="shrink-0 [&_svg]:size-4">
                    {entry.state === "done" ? (
                      <Check className="text-emerald-600" />
                    ) : entry.state === "error" ? (
                      <CircleAlert className="text-red-600" />
                    ) : entry.state === "skipped" ? (
                      <MinusCircle className="text-zinc-400" />
                    ) : entry.state === "running" ? (
                      <Loader2 className="animate-spin text-brand-600" />
                    ) : (
                      <span className="block size-4 rounded-full border border-zinc-300" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{job.label}</span>
                    <span
                      className={cn(
                        "block truncate text-xs",
                        entry.state === "error" ? "text-red-600" : "text-zinc-500",
                      )}
                    >
                      {entry.state === "done"
                        ? `„${entry.title}“ angelegt`
                        : entry.state === "error"
                          ? entry.message
                          : entry.state === "skipped"
                            ? "Übersprungen"
                            : entry.state === "running"
                              ? "Wird angelegt …"
                              : "Wartet"}
                    </span>
                  </span>
                  {entry.state === "done" ? (
                    <Link
                      to="/o/$orgId/documents/$documentId"
                      params={{ orgId: env.orgId, documentId: entry.documentId }}
                      onClick={() => onOpenChange(false)}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 [&_svg]:size-3.5"
                    >
                      Öffnen <ExternalLink />
                    </Link>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        <DialogFooter>
          {started ? (
            <>
              <span className="mr-auto self-center text-sm text-zinc-500">
                {running
                  ? `${doneCount} von ${jobs.length} angelegt …`
                  : `${doneCount} von ${jobs.length} ${jobs.length === 1 ? "Datei" : "Dateien"} angelegt`}
              </span>
              <Button disabled={running} onClick={() => onOpenChange(false)}>
                Fertig
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button disabled={chosen.length === 0} onClick={() => void start()}>
                {together || chosen.length <= 1
                  ? "Datei anlegen"
                  : `${chosen.length} Dateien anlegen`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
